import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import InfrastructureLeasingBuyerPostSaleMilestones from '../../components/infrastructureLeasing/InfrastructureLeasingBuyerPostSaleMilestones';
import InfrastructureLeasingBuyerPostSaleTabs from '../../components/infrastructureLeasing/InfrastructureLeasingBuyerPostSaleTabs';
import InfrastructureLeasingDisputePanel from '../../components/infrastructureLeasing/InfrastructureLeasingDisputePanel';
import {
  fetchBuyerLeaseTrackingDetail,
  postBuyerInfrastructureLeaseActivate,
  postBuyerTerminateLeaseEarly,
  postBuyerRequestMutualLeaseTermination,
  postBuyerConfirmMutualLeaseTermination,
  postBuyerWithdrawMutualLeaseTermination,
  postBuyerRejectMutualLeaseTermination,
  postBuyerCreateInfrastructurePaymentOrder,
  getBuyerCommercialDocuments,
  downloadBuyerCommercialDocument,
} from '../../services/infrastructureLeasingListing.service';
import { resolvePublicFileUrl } from '../../constants/api-urls';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/infrastructure_leasing_buyer_post_sale.scss';
import '../../assets/styles/infrastructure_leasing_buyer_lease_tracking.scss';

function formatDateOnly(iso) {
  if (!iso) return '—';
  try {
    const d = String(iso).slice(0, 10);
    return new Date(`${d}T12:00:00`).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function formatInrAmount(value) {
  if (value == null || value === '') return '—';
  const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, ''));
  if (!Number.isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return String(value);
  }
}

const BUYER_COMMERCIAL_DOC_TYPES = new Set([
  'buyer_tax_invoice',
  'payment_receipt',
  'refund_note',
  'credit_note',
  'debit_note',
  'termination_settlement_statement',
]);

function formatCommercialDocTypeLabel(t) {
  const key = String(t || '').toLowerCase();
  const labels = {
    buyer_tax_invoice: 'Buyer tax invoice',
    seller_payout_advice: 'Seller payout advice',
    platform_commission_invoice: 'Platform commission invoice',
    payment_receipt: 'Payment receipt',
    refund_note: 'Refund note',
    credit_note: 'Credit note',
    debit_note: 'Debit note',
    termination_settlement_statement: 'Termination settlement statement',
  };
  return labels[key] || key.replace(/_/g, ' ') || '—';
}

function formatCommercialDocCurrencyAmount(value, currency) {
  if (value == null || value === '') return '—';
  const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, ''));
  if (!Number.isFinite(n)) return '—';
  const cur = (currency && String(currency).trim()) || 'INR';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur, maximumFractionDigits: 2 }).format(n);
  } catch {
    return `${cur} ${n.toFixed(2)}`;
  }
}

function formatCommercialDocStatus(status) {
  const s = String(status || '').toLowerCase();
  return s ? s.replace(/_/g, ' ') : '—';
}

function settlementCardTitle(tf) {
  if (!tf) return 'Termination settlement';
  if (tf.terminationType === 'mutual') return 'Lease terminated by mutual agreement';
  if (tf.terminatedByRole === 'buyer') return 'Early termination by buyer';
  return 'Termination by seller';
}

function settlementStatusLabel(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'calculated') return 'Pending settlement';
  return s ? s.replace(/_/g, ' ') : '—';
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(iso);
  }
}

function lifecycleBadgeClass(key) {
  if (key === 'active') return 'success';
  if (key === 'pending') return 'warning';
  if (key === 'completed') return 'secondary';
  if (key === 'terminated') return 'danger';
  return 'secondary';
}

function timelinePrefix(state) {
  if (state === 'done') return '✓ ';
  if (state === 'current') return '● ';
  return '○ ';
}

export default function InfrastructureLeasingBuyerLeaseTrackingPage() {
  const { listingId: listingParam, enquiryId: enquiryParam } = useParams();
  const navigate = useNavigate();
  const listingIdNum = useMemo(() => {
    const n = parseInt(String(listingParam), 10);
    return Number.isFinite(n) && n >= 1 ? n : NaN;
  }, [listingParam]);
  const enquiryIdNum = useMemo(() => {
    const n = parseInt(String(enquiryParam), 10);
    return Number.isFinite(n) && n >= 1 ? n : NaN;
  }, [enquiryParam]);

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [activating, setActivating] = useState(false);
  const [terminating, setTerminating] = useState(false);
  const [mutualBusy, setMutualBusy] = useState(false);
  const [paymentOrderBusy, setPaymentOrderBusy] = useState(false);
  const [commercialDocs, setCommercialDocs] = useState([]);
  const [commercialDocsLoading, setCommercialDocsLoading] = useState(false);
  const [commercialDocDownloadId, setCommercialDocDownloadId] = useState(null);

  const basePath = `/infrastructure-leasing/my-enquiries/${listingIdNum}/${enquiryIdNum}`;

  const load = useCallback(async () => {
    if (!Number.isFinite(listingIdNum) || !Number.isFinite(enquiryIdNum)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetchBuyerLeaseTrackingDetail(listingIdNum, enquiryIdNum);
      if (!res?.success || !res.data) {
        setDetail(null);
        toast.error(res?.message || 'Could not load lease tracking.');
        return;
      }
      let data = res.data;
      const st = String(data?.leaseOverview?.pipelineStage || '').toLowerCase();
      if (st === 'invoice_issued') {
        const act = await postBuyerInfrastructureLeaseActivate(listingIdNum, enquiryIdNum);
        if (act?.success) {
          const again = await fetchBuyerLeaseTrackingDetail(listingIdNum, enquiryIdNum);
          if (again?.success && again.data) {
            data = again.data;
          }
        } else {
          toast.warning(act?.message || 'Lease activation will complete once ready.');
        }
      }
      setDetail(data);
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.message || e?.message;
      if (status === 403 || status === 404) {
        toast.error(msg || 'Could not load this lease.');
        navigate('/infrastructure-leasing/my-enquiries', { replace: true });
      } else if (status === 409) {
        toast.warning(msg || 'Complete earlier steps first.');
        navigate(`${basePath}/agreement`, { replace: true });
      } else {
        toast.error(msg || 'Could not load lease tracking.');
      }
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [listingIdNum, enquiryIdNum, navigate, basePath]);

  const retryActivateLease = useCallback(async () => {
    if (!Number.isFinite(listingIdNum) || !Number.isFinite(enquiryIdNum)) return;
    setActivating(true);
    try {
      const act = await postBuyerInfrastructureLeaseActivate(listingIdNum, enquiryIdNum);
      if (act?.success) {
        toast.success(act.message || 'Lease activated.');
        await load();
      } else {
        toast.error(act?.message || 'Activation failed.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Activation failed.');
    } finally {
      setActivating(false);
    }
  }, [listingIdNum, enquiryIdNum, load]);

  const handleTerminateLeaseEarly = useCallback(async () => {
    if (!Number.isFinite(listingIdNum) || !Number.isFinite(enquiryIdNum)) return;
    const ok = window.confirm(
      'Terminate this lease early? This ends the lease on the platform (inventory released, agreement cancelled). It does not replace refunds or settlement — contact the seller or support if needed. This is not a mutual approval flow.'
    );
    if (!ok) return;
    setTerminating(true);
    try {
      const res = await postBuyerTerminateLeaseEarly(listingIdNum, enquiryIdNum, {});
      if (res?.success) {
        toast.success(res.message || 'Lease terminated early.');
        await load();
      } else {
        toast.error(res?.message || 'Could not terminate lease.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not terminate lease.');
    } finally {
      setTerminating(false);
    }
  }, [listingIdNum, enquiryIdNum, load]);

  const handleRequestMutualTermination = useCallback(async () => {
    if (!Number.isFinite(listingIdNum) || !Number.isFinite(enquiryIdNum)) return;
    const ok = window.confirm(
      'Propose ending this lease by mutual agreement? The seller must confirm before the lease ends on the platform (same outcome as early termination once confirmed).'
    );
    if (!ok) return;
    setMutualBusy(true);
    try {
      const res = await postBuyerRequestMutualLeaseTermination(listingIdNum, enquiryIdNum, {});
      if (res?.success) {
        toast.success(res.message || 'Mutual termination proposed.');
        await load();
      } else {
        toast.error(res?.message || 'Could not propose mutual termination.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not propose mutual termination.');
    } finally {
      setMutualBusy(false);
    }
  }, [listingIdNum, enquiryIdNum, load]);

  const handleConfirmMutualTermination = useCallback(async () => {
    if (!Number.isFinite(listingIdNum) || !Number.isFinite(enquiryIdNum)) return;
    const ok = window.confirm(
      'Confirm mutual lease termination? This runs the same early-termination steps as a unilateral exit (allocation released, agreement cancelled, enquiry withdrawn).'
    );
    if (!ok) return;
    setMutualBusy(true);
    try {
      const res = await postBuyerConfirmMutualLeaseTermination(listingIdNum, enquiryIdNum, {});
      if (res?.success) {
        toast.success(res.message || 'Mutual termination confirmed.');
        await load();
      } else {
        toast.error(res?.message || 'Could not confirm mutual termination.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not confirm mutual termination.');
    } finally {
      setMutualBusy(false);
    }
  }, [listingIdNum, enquiryIdNum, load]);

  const handleWithdrawMutualTermination = useCallback(async () => {
    if (!Number.isFinite(listingIdNum) || !Number.isFinite(enquiryIdNum)) return;
    const ok = window.confirm('Withdraw your mutual termination request? The lease stays active until ended another way.');
    if (!ok) return;
    setMutualBusy(true);
    try {
      const res = await postBuyerWithdrawMutualLeaseTermination(listingIdNum, enquiryIdNum, {});
      if (res?.success) {
        toast.success(res.message || 'Request withdrawn.');
        await load();
      } else {
        toast.error(res?.message || 'Could not withdraw request.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not withdraw request.');
    } finally {
      setMutualBusy(false);
    }
  }, [listingIdNum, enquiryIdNum, load]);

  const handleRejectMutualTermination = useCallback(async () => {
    if (!Number.isFinite(listingIdNum) || !Number.isFinite(enquiryIdNum)) return;
    const ok = window.confirm('Reject the seller’s mutual termination proposal? The lease stays active.');
    if (!ok) return;
    const reasonRaw = window.prompt('Optional reason (max ~500 chars, leave blank to skip):', '') || '';
    setMutualBusy(true);
    try {
      const res = await postBuyerRejectMutualLeaseTermination(listingIdNum, enquiryIdNum, {
        reason: reasonRaw.trim() || undefined,
      });
      if (res?.success) {
        toast.success(res.message || 'Request rejected.');
        await load();
      } else {
        toast.error(res?.message || 'Could not reject request.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not reject request.');
    } finally {
      setMutualBusy(false);
    }
  }, [listingIdNum, enquiryIdNum, load]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!Number.isFinite(listingIdNum) || !Number.isFinite(enquiryIdNum)) {
      setCommercialDocs([]);
      return undefined;
    }
    let cancelled = false;
    setCommercialDocsLoading(true);
    (async () => {
      try {
        const res = await getBuyerCommercialDocuments(listingIdNum, enquiryIdNum);
        if (cancelled) return;
        if (res?.success && Array.isArray(res.data?.items)) {
          setCommercialDocs(res.data.items);
        } else {
          setCommercialDocs([]);
        }
      } catch {
        if (!cancelled) setCommercialDocs([]);
      } finally {
        if (!cancelled) setCommercialDocsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [listingIdNum, enquiryIdNum, detail]);

  const filteredBuyerCommercialDocs = useMemo(
    () =>
      commercialDocs.filter((d) =>
        BUYER_COMMERCIAL_DOC_TYPES.has(String(d.documentType || '').toLowerCase())
      ),
    [commercialDocs]
  );

  const handleDownloadBuyerCommercialDoc = useCallback(
    async (doc) => {
      const id = doc?.id != null ? Math.trunc(Number(doc.id)) : NaN;
      if (!Number.isFinite(id) || id < 1) return;
      const safeName =
        doc.documentNumber && String(doc.documentNumber).trim()
          ? `${String(doc.documentNumber).trim().replace(/[^\w.-]+/g, '_')}.pdf`
          : `commercial-document-${id}.pdf`;
      setCommercialDocDownloadId(id);
      try {
        await downloadBuyerCommercialDocument(listingIdNum, enquiryIdNum, id, safeName);
      } catch (e) {
        toast.error(e?.message || e?.response?.data?.message || 'PDF download failed. Please retry.');
      } finally {
        setCommercialDocDownloadId(null);
      }
    },
    [listingIdNum, enquiryIdNum]
  );

  const ov = detail?.leaseOverview;
  const docUrl =
    detail?.agreement?.agreementDocumentPath &&
    String(detail.agreement.agreementDocumentPath).trim()
      ? resolvePublicFileUrl(detail.agreement.agreementDocumentPath)
      : '';

  if (!Number.isFinite(listingIdNum) || !Number.isFinite(enquiryIdNum)) {
    return (
      <div className="container-fluid ile-buyer-post-sale py-4">
        <p className="text-muted">Invalid link.</p>
        <Link to="/infrastructure-leasing/my-enquiries">Back to My enquiries</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-fluid ile-buyer-post-sale py-4">
        <div className="text-muted py-5">Loading lease tracking…</div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="container-fluid ile-buyer-post-sale py-4">
        <p className="text-muted">Nothing to show.</p>
        <Link to="/infrastructure-leasing/my-enquiries">Back to My enquiries</Link>
      </div>
    );
  }

  const seller = detail.seller || {};
  const buyer = detail.buyer || {};
  const asset = detail.asset || {};
  const fin = detail.financial || {};
  const agreements = detail.agreement || {};
  const actions = detail.actions || {};
  const mt = detail.mutualLeaseTermination || {};
  const tf = detail.terminationFinancials || null;
  const ps = detail.paymentSettlementSummary || {};
  const cds = detail.commercialDocumentSummary || {};
  const recon = String(ps.reconciliationStatus || 'no_payment').toLowerCase();
  const payStatus = String(ps.paymentStatus || '').toLowerCase();

  const parsePendingPaymentAmount = () => {
    const raw = fin.pendingAmountLabel;
    if (raw == null || String(raw).trim() === '') return null;
    const n = parseFloat(String(raw).replace(/[^\d.]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  const handleCreatePaymentOrder = async () => {
    const amt = parsePendingPaymentAmount();
    if (amt == null) {
      toast.error('No payable amount found on file. Complete agreement steps or enter an amount in payment flow.');
      return;
    }
    setPaymentOrderBusy(true);
    try {
      const res = await postBuyerCreateInfrastructurePaymentOrder(listingIdNum, enquiryIdNum, { amount: amt });
      if (res?.success && res.data) {
        toast.success(res.message || 'Payment order created — complete payment with your bank/app.');
        await load();
      } else {
        toast.error(res?.message || 'Could not create payment order.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not create payment order.');
    } finally {
      setPaymentOrderBusy(false);
    }
  };
  const showMutualGovernanceCard =
    !!mt.status ||
    !!mt.canRequest ||
    !!mt.canConfirm ||
    !!mt.canWithdraw ||
    !!mt.canReject ||
    (Array.isArray(mt.timeline) && mt.timeline.length > 0);

  const sellerMail = seller.email && String(seller.email).trim() ? `mailto:${encodeURIComponent(seller.email.trim())}` : null;
  const sellerTel = seller.phone && String(seller.phone).trim().replace(/\s+/g, '')
    ? `tel:${String(seller.phone).trim()}`
    : null;

  return (
    <div className="container-fluid ile-buyer-post-sale py-4 ile-lease-tracking">
      <div className="ile-buyer-post-sale__shell">
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <Link to="/dashboard/buyer">Buyer dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/infrastructure-leasing/my-enquiries">My enquiries</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Lease tracking
            </li>
          </ol>
        </nav>

        <InfrastructureLeasingBuyerPostSaleTabs basePath={basePath} />

        <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
          <div>
            <h1 className="h4 mb-1">Lease tracking</h1>
            <p className="text-muted small mb-0">
              {detail.listingTitle ? (
                <>
                  <span className="fw-semibold text-body">{detail.listingTitle}</span>
                  {ov?.agreementId ? <span className="text-muted"> · {ov.agreementId}</span> : null}
                </>
              ) : (
                'Infrastructure leasing'
              )}
            </p>
          </div>
          {ov?.lifecycleStatusLabel ? (
            <span
              className={`badge rounded-pill bg-${lifecycleBadgeClass(ov.lifecycleStatus)} px-3 py-2 ${
                ov.lifecycleStatus === 'pending' ? 'text-dark' : ''
              }`}
            >
              {ov.lifecycleStatusLabel}
            </span>
          ) : null}
        </div>

        {/* 1. Lease overview */}
        <div className="card border shadow-sm mb-3">
          <div className="card-header bg-white fw-semibold py-2">Lease overview</div>
          <div className="card-body">
            <dl className="ile-lease-tracking__overview-grid mb-0">
              <div className="ile-lease-tracking__overview-item">
                <dt>Lease ID</dt>
                <dd>{ov?.leaseId || '—'}</dd>
              </div>
              <div className="ile-lease-tracking__overview-item">
                <dt>Agreement ID</dt>
                <dd>{ov?.agreementId || '—'}</dd>
              </div>
              <div className="ile-lease-tracking__overview-item">
                <dt>Pipeline</dt>
                <dd className="text-capitalize">{String(ov?.pipelineStage || '—').replace(/_/g, ' ')}</dd>
              </div>
              <div className="ile-lease-tracking__overview-item">
                <dt>Lease start</dt>
                <dd>{formatDateOnly(ov?.leaseStartDate)}</dd>
              </div>
              <div className="ile-lease-tracking__overview-item">
                <dt>Lease end</dt>
                <dd>{formatDateOnly(ov?.leaseEndDate)}</dd>
              </div>
              <div className="ile-lease-tracking__overview-item">
                <dt>Remaining</dt>
                <dd>{ov?.remainingDurationLabel || '—'}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* 2. Asset */}
        <div className="card border shadow-sm mb-3">
          <div className="card-header bg-white fw-semibold py-2">Property / asset</div>
          <div className="card-body small">
            <p className="fw-semibold mb-1">{asset.assetName || '—'}</p>
            <p className="text-muted mb-2">
              {[asset.assetTypeLabel, asset.listingCategoryLabel].filter(Boolean).join(' · ') || '—'}
            </p>
            <p className="mb-2">
              <span className="text-muted">Location:</span> {asset.locationLine || '—'}
            </p>
            {asset.keySpecifications ? (
              <div className="border rounded p-2 bg-light ile-lease-tracking__terms-box">
                {asset.keySpecifications}
              </div>
            ) : asset.description ? (
              <div className="border rounded p-2 bg-light ile-lease-tracking__terms-box">{asset.description}</div>
            ) : (
              <p className="text-muted mb-0">No specifications on file.</p>
            )}
          </div>
        </div>

        {/* 3. Parties */}
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <div className="ile-lease-tracking__party-card">
              <div className="ile-lease-tracking__party-role">Seller (lessor)</div>
              <div className="fw-semibold">{seller.displayName || '—'}</div>
              {seller.companyName ? <div className="small text-muted">{seller.companyName}</div> : null}
              <hr className="my-2" />
              <div className="small">
                {seller.email ? (
                  <div>
                    Email:{' '}
                    <a href={sellerMail || '#'} className="text-break">
                      {seller.email}
                    </a>
                  </div>
                ) : (
                  <div className="text-muted">Email not available</div>
                )}
                {seller.phone ? (
                  <div>
                    Phone:{' '}
                    <a href={sellerTel || '#'}>{seller.phone}</a>
                  </div>
                ) : null}
                {seller.addressLine ? <div className="mt-1 text-muted">{seller.addressLine}</div> : null}
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="ile-lease-tracking__party-card">
              <div className="ile-lease-tracking__party-role">Buyer (lessee)</div>
              <div className="fw-semibold">{buyer.displayName || '—'}</div>
              {buyer.companyName ? <div className="small text-muted">{buyer.companyName}</div> : null}
              <hr className="my-2" />
              <div className="small">
                {buyer.email ? (
                  <div>
                    Email:{' '}
                    <a href={`mailto:${encodeURIComponent(String(buyer.email).trim())}`} className="text-break">
                      {buyer.email}
                    </a>
                  </div>
                ) : (
                  <div className="text-muted">Email not available</div>
                )}
                {buyer.phone ? (
                  <div>
                    Phone:{' '}
                    <a href={`tel:${String(buyer.phone).trim().replace(/\s+/g, '')}`}>{buyer.phone}</a>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {/* 4. Financial */}
        <div className="card border shadow-sm mb-3">
          <div className="card-header bg-white fw-semibold py-2">Financial details</div>
          <div className="card-body">
            <dl className="row mb-0 small ile-post-sale-dl gy-2">
              <dt className="col-sm-4 text-muted">Total lease (estimate)</dt>
              <dd className="col-sm-8">{fin.totalLeaseValueLabel || '—'}</dd>
              <dt className="col-sm-4 text-muted">Rental amount</dt>
              <dd className="col-sm-8">{fin.rentalAmountLabel || '—'}</dd>
              <dt className="col-sm-4 text-muted">Payment frequency</dt>
              <dd className="col-sm-8">{fin.paymentFrequencyLabel || '—'}</dd>
              <dt className="col-sm-4 text-muted">Paid (initial)</dt>
              <dd className="col-sm-8">{fin.paidAmountLabel || '—'}</dd>
              <dt className="col-sm-4 text-muted">Pending</dt>
              <dd className="col-sm-8">{fin.pendingAmountLabel || '—'}</dd>
              <dt className="col-sm-4 text-muted">Security deposit</dt>
              <dd className="col-sm-8">{fin.securityDeposit || '—'}</dd>
              <dt className="col-sm-4 text-muted">Next payment due</dt>
              <dd className="col-sm-8">
                {fin.nextPaymentDueDate ? formatDateTime(fin.nextPaymentDueDate) : '—'}
                {fin.nextPaymentHint ? (
                  <div className="text-muted mt-1 small">{fin.nextPaymentHint}</div>
                ) : null}
              </dd>
            </dl>
          </div>
        </div>

        <div className="card border shadow-sm mb-3">
          <div className="card-header bg-white fw-semibold py-2">Ledger &amp; settlement summary</div>
          <div className="card-body small">
            <p className="text-muted mb-2 mb-md-3">
              Payments are processed through authorised payment partners. The platform does not operate an escrow account or
              issue stored-value wallets. Ledger entries shown here are for transaction tracking and settlement visibility
              only.
            </p>
            {recon === 'no_payment' && payStatus !== 'payment_pending' && payStatus !== 'paid' ? (
              <p className="mb-2">
                <span className="text-muted">Status:</span> Payment not initiated
              </p>
            ) : null}
            {payStatus === 'payment_pending' || recon === 'payment_pending' ? (
              <p className="mb-2">
                <span className="text-muted">Status:</span> Payment pending — complete checkout with your payment partner.
              </p>
            ) : null}
            {payStatus === 'paid' || recon === 'paid_not_settled' || recon === 'settlement_pending' || recon === 'fully_settled' ? (
              <p className="mb-2">
                <span className="text-muted">Payment:</span> Successful (recorded){' '}
                {ps.totalPaid ? <span className="fw-semibold">· Paid {formatInrAmount(ps.totalPaid)}</span> : null}
              </p>
            ) : null}
            {recon === 'refund_pending' ? (
              <p className="mb-2 text-warning">Refund due — pending processing via payment partner.</p>
            ) : null}
            {recon === 'refund_processed' ? (
              <p className="mb-2 text-success">Refund processed (ledger updated).</p>
            ) : null}
            {recon === 'exception' ? (
              <p className="mb-2 text-danger">Exception — please contact support with your enquiry reference.</p>
            ) : null}
            <dl className="row mb-2 gy-1">
              <dt className="col-sm-5 text-muted">Buyer payable (open)</dt>
              <dd className="col-sm-7 mb-0">{formatInrAmount(ps.buyerPayableOpen)}</dd>
              <dt className="col-sm-5 text-muted">Refund due</dt>
              <dd className="col-sm-7 mb-0">{formatInrAmount(ps.refundDue)}</dd>
              <dt className="col-sm-5 text-muted">Penalty due</dt>
              <dd className="col-sm-7 mb-0">{formatInrAmount(ps.penaltyDue)}</dd>
              <dt className="col-sm-5 text-muted">Reconciliation</dt>
              <dd className="col-sm-7 mb-0 text-break">{String(ps.reconciliationStatus || '—').replace(/_/g, ' ')}</dd>
            </dl>
            {String(ov?.pipelineStage || '').toLowerCase() === 'payment_due' &&
            recon === 'no_payment' &&
            payStatus !== 'payment_pending' ? (
              <button
                type="button"
                className="btn btn-dark btn-sm"
                disabled={paymentOrderBusy}
                onClick={() => void handleCreatePaymentOrder()}
              >
                {paymentOrderBusy ? 'Creating…' : 'Pay now'}
              </button>
            ) : null}
            <p className="text-muted mt-3 mb-0 small">
              This is an internal ledger record and not a stored wallet balance.
            </p>
          </div>
        </div>

        {detail?.notificationSummary ? (
          <div className="card border shadow-sm mb-3">
            <div className="card-header bg-white fw-semibold py-2">Notifications</div>
            <div className="card-body small">
              <p className="mb-1">
                Unread for this lease:{' '}
                <strong>{detail.notificationSummary.unreadCountForEnquiry ?? 0}</strong>
              </p>
              {detail.notificationSummary.latestNotificationTitle ? (
                <p className="text-muted mb-0">
                  Latest: {detail.notificationSummary.latestNotificationTitle}
                  {detail.notificationSummary.latestNotificationAt ? (
                    <span className="text-muted"> · {formatDateTime(detail.notificationSummary.latestNotificationAt)}</span>
                  ) : null}
                </p>
              ) : (
                <p className="text-muted mb-0">No in-app notifications yet for this enquiry.</p>
              )}
              <p className="mt-2 mb-0 small text-muted">
                Open the bell icon in the header for all notifications and to mark items as read.
              </p>
            </div>
          </div>
        ) : null}

        {Number.isFinite(listingIdNum) && Number.isFinite(enquiryIdNum) ? (
          <InfrastructureLeasingDisputePanel
            variant="buyer"
            listingIdNum={listingIdNum}
            enquiryIdNum={enquiryIdNum}
            disputeSummary={detail?.disputeSummary}
            onRefresh={load}
          />
        ) : null}

        <div className="card border shadow-sm mb-3">
          <div className="card-header bg-white fw-semibold py-2">Commercial documents</div>
          <div className="card-body small">
            <p className="text-muted mb-3">
              Commercial documents are generated from payment, settlement and tax records. GST/e-invoice details are subject
              to finance approval where applicable.
            </p>
            {(cds.totalDocuments > 0 || cds.hasTaxReviewPending) && (
              <dl className="row mb-3 gy-1">
                <dt className="col-sm-4 text-muted">Documents on file</dt>
                <dd className="col-sm-8 mb-0">{cds.totalDocuments != null ? cds.totalDocuments : '—'}</dd>
                <dt className="col-sm-4 text-muted">Pending approval</dt>
                <dd className="col-sm-8 mb-0">{cds.pendingApprovalCount != null ? cds.pendingApprovalCount : '—'}</dd>
                <dt className="col-sm-4 text-muted">Finalised</dt>
                <dd className="col-sm-8 mb-0">{cds.finalisedCount != null ? cds.finalisedCount : '—'}</dd>
                <dt className="col-sm-4 text-muted">Downloadable PDFs</dt>
                <dd className="col-sm-8 mb-0">{cds.downloadableCount != null ? cds.downloadableCount : '—'}</dd>
                {cds.latestInvoiceNumber ? (
                  <>
                    <dt className="col-sm-4 text-muted">Latest invoice</dt>
                    <dd className="col-sm-8 mb-0 text-break">{cds.latestInvoiceNumber}</dd>
                  </>
                ) : null}
                {cds.latestReceiptNumber ? (
                  <>
                    <dt className="col-sm-4 text-muted">Latest receipt</dt>
                    <dd className="col-sm-8 mb-0 text-break">{cds.latestReceiptNumber}</dd>
                  </>
                ) : null}
                {cds.hasTaxReviewPending ? (
                  <dd className="col-12 mb-0">
                    <span className="badge bg-warning text-dark">Tax review pending</span>
                  </dd>
                ) : null}
              </dl>
            )}
            {commercialDocsLoading ? (
              <p className="text-muted mb-0">Loading documents…</p>
            ) : filteredBuyerCommercialDocs.length === 0 ? (
              <p className="text-muted mb-0">No commercial documents yet for this enquiry.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-bordered align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th scope="col">Document type</th>
                      <th scope="col">Document number</th>
                      <th scope="col">Status</th>
                      <th scope="col" className="text-end">
                        Amount
                      </th>
                      <th scope="col">Country / regime</th>
                      <th scope="col">Generated</th>
                      <th scope="col" className="text-end">
                        Download
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBuyerCommercialDocs.map((row) => {
                      const hasPdf = row.pdfFilePath != null && String(row.pdfFilePath).trim() !== '';
                      const downloading = commercialDocDownloadId === row.id;
                      return (
                        <tr key={row.id}>
                          <td>{formatCommercialDocTypeLabel(row.documentType)}</td>
                          <td className="text-break">{row.documentNumber || '—'}</td>
                          <td>{formatCommercialDocStatus(row.documentStatus)}</td>
                          <td className="text-end text-nowrap">
                            {formatCommercialDocCurrencyAmount(row.totalDocumentValue, row.currency)}
                          </td>
                          <td className="small">
                            <div>{row.countryCode || '—'}</div>
                            <div className="text-muted">{row.taxRegimeName || row.taxType || ''}</div>
                          </td>
                          <td className="text-nowrap">{formatDateTime(row.createdAt)}</td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-outline-dark btn-sm"
                              disabled={!hasPdf || downloading}
                              title={hasPdf ? 'Download PDF' : 'PDF not generated yet'}
                              onClick={() => void handleDownloadBuyerCommercialDoc(row)}
                            >
                              {downloading ? '…' : 'Download'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {tf ? (
          <div className="card border shadow-sm mb-3 border-secondary">
            <div className="card-header bg-white fw-semibold py-2">{settlementCardTitle(tf)}</div>
            <div className="card-body small">
              <dl className="row mb-2 gy-1">
                <dt className="col-sm-4 text-muted">Utilized</dt>
                <dd className="col-sm-8 mb-0">{formatInrAmount(tf.utilizedValue)}</dd>
                <dt className="col-sm-4 text-muted">Unused</dt>
                <dd className="col-sm-8 mb-0">{formatInrAmount(tf.unusedValue)}</dd>
                {tf.terminationType === 'mutual' ? (
                  <>
                    <dt className="col-sm-4 text-muted">Refund</dt>
                    <dd className="col-sm-8 mb-0">{formatInrAmount(tf.refund)}</dd>
                  </>
                ) : null}
                {tf.terminationType === 'unilateral' && tf.terminatedByRole === 'buyer' ? (
                  <>
                    <dt className="col-sm-4 text-muted">Penalty</dt>
                    <dd className="col-sm-8 mb-0">{formatInrAmount(tf.penalty)}</dd>
                    <dt className="col-sm-4 text-muted">Refund</dt>
                    <dd className="col-sm-8 mb-0">{formatInrAmount(tf.refund)}</dd>
                  </>
                ) : null}
                {tf.terminationType === 'unilateral' && tf.terminatedByRole === 'seller' ? (
                  <>
                    <dt className="col-sm-4 text-muted">Compensation</dt>
                    <dd className="col-sm-8 mb-0">{formatInrAmount(tf.penalty)}</dd>
                    <dt className="col-sm-4 text-muted">Total refund</dt>
                    <dd className="col-sm-8 mb-0">{formatInrAmount(tf.refund)}</dd>
                  </>
                ) : null}
                {(() => {
                  const p = tf.payable != null ? parseFloat(String(tf.payable).replace(/,/g, '')) : NaN;
                  return Number.isFinite(p) && p > 0;
                })() ? (
                  <>
                    <dt className="col-sm-4 text-muted">Payable</dt>
                    <dd className="col-sm-8 mb-0">{formatInrAmount(tf.payable)}</dd>
                  </>
                ) : null}
                <dt className="col-sm-4 text-muted">Direction</dt>
                <dd className="col-sm-8 mb-0 text-break">{String(tf.settlementDirection || '—').replace(/_/g, ' ')}</dd>
              </dl>
              <p className="text-muted mb-0 small">
                Status: <span className="text-body">{settlementStatusLabel(tf.settlementStatus)}</span>
              </p>
            </div>
          </div>
        ) : null}

        {/* 5. Payment history */}
        <div className="card border shadow-sm mb-3">
          <div className="card-header bg-white fw-semibold py-2">Payment history</div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-sm table-striped mb-0 small">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Transaction / ref.</th>
                    <th className="text-end">Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.paymentHistory?.length ? (
                    detail.paymentHistory.map((row, idx) => (
                      <tr key={`${row.kind}-${idx}`}>
                        <td>{row.paymentDate ? formatDateTime(row.paymentDate) : '—'}</td>
                        <td>{row.amountLabel || '—'}</td>
                        <td className="text-capitalize">{row.status}</td>
                        <td className="font-monospace small">{row.transactionId || row.invoiceReference || '—'}</td>
                        <td className="text-end">
                          {row.status === 'success' ? (
                            <Link to={`${basePath}/invoice`} className="btn btn-sm btn-outline-secondary">
                              Invoice
                            </Link>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-muted px-3 py-3">
                        No payments recorded yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* 6. Agreement */}
        <div className="card border shadow-sm mb-3">
          <div className="card-header bg-white fw-semibold py-2">Agreement</div>
          <div className="card-body small">
            <p className="mb-2">
              <span className="text-muted">Seller signed:</span> {formatDateTime(agreements.sellerSignedAt)}
              <span className="text-muted ms-3">Buyer signed:</span> {formatDateTime(agreements.buyerSignedAt)}
            </p>
            {docUrl ? (
              <p className="mb-3">
                <a className="btn btn-sm btn-outline-primary me-2" href={docUrl} target="_blank" rel="noopener noreferrer">
                  View agreement PDF
                </a>
                <a className="btn btn-sm btn-outline-secondary" href={docUrl} download>
                  Download
                </a>
              </p>
            ) : (
              <p className="text-muted mb-3">No agreement document uploaded.</p>
            )}
            {agreements.paymentTermsSnippet ? (
              <div className="mb-2">
                <div className="fw-semibold mb-1">Payment terms</div>
                <div className="border rounded p-2 bg-light ile-lease-tracking__terms-box">{agreements.paymentTermsSnippet}</div>
              </div>
            ) : null}
            {agreements.termsSummary ? (
              <div className="mb-2">
                <div className="fw-semibold mb-1">Terms &amp; conditions (summary)</div>
                <div className="border rounded p-2 bg-light ile-lease-tracking__terms-box">{agreements.termsSummary}</div>
              </div>
            ) : null}
            {agreements.renewalTermsSnippet ? (
              <div>
                <div className="fw-semibold mb-1">Renewal</div>
                <div className="border rounded p-2 bg-light ile-lease-tracking__terms-box">{agreements.renewalTermsSnippet}</div>
              </div>
            ) : null}
          </div>
        </div>

        {/* 7. Milestones (compact) + extended timeline */}
        {detail.milestones?.length ? (
          <div className="mb-2">
            <div className="small text-muted mb-1">Onboarding milestones</div>
            <InfrastructureLeasingBuyerPostSaleMilestones milestones={detail.milestones} />
          </div>
        ) : null}

        <div className="card border shadow-sm mb-3">
          <div className="card-header bg-white fw-semibold py-2">Lease lifecycle</div>
          <div className="card-body py-3">
            <div className="ile-lease-tracking__timeline">
              {(detail.timelineSteps || []).map((step) => (
                <div
                  key={step.key}
                  className={`ile-lease-tracking__timeline-step ile-lease-tracking__timeline-step--${step.state}`}
                >
                  <div className="ile-lease-tracking__timeline-label">
                    {timelinePrefix(step.state)}
                    {step.label}
                  </div>
                  <div className="ile-lease-tracking__timeline-meta">
                    {step.completedAt ? formatDateOnly(step.completedAt) : step.detail || '\u00a0'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 8. Notifications */}
        {detail.alerts?.length ? (
          <div className="mb-3">
            <h2 className="h6 fw-semibold mb-2">Notifications</h2>
            {detail.alerts.map((a) => (
              <div
                key={a.code}
                className={`alert alert-${a.severity === 'danger' ? 'danger' : a.severity === 'warning' ? 'warning' : 'info'} py-2 small mb-2`}
                role="status"
              >
                <strong>{a.title}</strong>
                <span className="ms-1">{a.message}</span>
              </div>
            ))}
          </div>
        ) : null}

        {showMutualGovernanceCard ? (
          <div className="card border shadow-sm mb-3">
            <div className="card-header bg-white fw-semibold py-2">Mutual termination</div>
            <div className="card-body py-3 small">
              {mt.status === 'pending' && mt.pendingInitiatedBy === 'buyer' ? (
                <p className="text-muted mb-2 mb-md-3">Awaiting seller confirmation.</p>
              ) : null}
              {mt.status === 'pending' && mt.pendingInitiatedBy === 'seller' ? (
                <p className="text-muted mb-2 mb-md-3">Seller proposed mutual termination.</p>
              ) : null}
              {mt.status === 'pending' && mt.expiresAt ? (
                <p className="mb-2">
                  <span className="text-muted">Expires on:</span> {formatDateTime(mt.expiresAt)}
                </p>
              ) : null}
              {mt.status === 'rejected' ? (
                <p className="mb-2">
                  <span className="text-danger">Request rejected.</span>
                  {mt.rejectionReason ? (
                    <>
                      {' '}
                      Reason: {mt.rejectionReason}
                    </>
                  ) : null}
                </p>
              ) : null}
              {mt.status === 'withdrawn' ? <p className="text-muted mb-2">Request withdrawn.</p> : null}
              {mt.status === 'expired' ? <p className="text-muted mb-2">Request expired.</p> : null}
              {Array.isArray(mt.timeline) && mt.timeline.length > 0 ? (
                <ul className="list-unstyled mb-0 ile-lease-tracking__timeline">
                  {mt.timeline.map((step) => (
                    <li key={step.key} className={`py-1 ile-lease-tracking__timeline-step--${step.state}`}>
                      {timelinePrefix(step.state)}
                      {step.label}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* 9. Actions */}
        <div className="card border shadow-sm mb-4 ile-lease-tracking__actions">
          <div className="card-header bg-white fw-semibold py-2">Actions</div>
          <div className="card-body d-flex flex-wrap gap-2">
            {actions.canMakePayment ? (
              <Link to={`${basePath}/payment`} className="btn btn-dark">
                Make payment
              </Link>
            ) : null}
            {actions.canActivateLease ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={activating}
                onClick={() => void retryActivateLease()}
              >
                {activating ? 'Activating…' : 'Activate lease'}
              </button>
            ) : null}
            <Link to={`${basePath}/invoice`} className="btn btn-outline-secondary">
              Download invoice
            </Link>
            {sellerMail ? (
              <a className="btn btn-outline-secondary" href={sellerMail}>
                Contact seller
              </a>
            ) : null}
            <Link to="/contact" className="btn btn-outline-secondary">
              Raise issue / support
            </Link>
            {actions.showRenewLease ? (
              <button type="button" className="btn btn-outline-primary" disabled title="Discuss renewal with the seller">
                Renew lease
              </button>
            ) : null}
            {actions.canTerminateLeaseEarly ? (
              <button
                type="button"
                className="btn btn-outline-danger"
                disabled={terminating || mutualBusy}
                onClick={() => void handleTerminateLeaseEarly()}
              >
                {terminating ? 'Terminating…' : 'Terminate lease early'}
              </button>
            ) : null}
            {mt.canRequest ? (
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={mutualBusy || terminating}
                onClick={() => void handleRequestMutualTermination()}
              >
                {mutualBusy ? 'Working…' : 'Propose mutual termination'}
              </button>
            ) : null}
            {mt.canWithdraw ? (
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={mutualBusy || terminating}
                onClick={() => void handleWithdrawMutualTermination()}
              >
                {mutualBusy ? 'Working…' : 'Withdraw request'}
              </button>
            ) : null}
            {mt.canConfirm ? (
              <button
                type="button"
                className="btn btn-outline-danger"
                disabled={mutualBusy || terminating}
                onClick={() => void handleConfirmMutualTermination()}
              >
                {mutualBusy ? 'Working…' : 'Confirm mutual termination'}
              </button>
            ) : null}
            {mt.canReject ? (
              <button
                type="button"
                className="btn btn-outline-dark"
                disabled={mutualBusy || terminating}
                onClick={() => void handleRejectMutualTermination()}
              >
                {mutualBusy ? 'Working…' : 'Reject proposal'}
              </button>
            ) : null}
          </div>
          {actions.showRenewLease ? (
            <div className="card-footer small text-muted bg-white border-top-0 pt-0">
              Renewal is coordinated with the seller—use Contact seller when your term is ending.
            </div>
          ) : null}
        </div>

        <div className="small mb-3">
          <Link to="/infrastructure-leasing/my-enquiries" className="text-muted">
            ← My enquiries
          </Link>
        </div>
      </div>
    </div>
  );
}
